/**
 * CampaignsTab — Onglet "Campagnes & Audit" (Iter386 Lot B)
 *
 * Liste paginée des campagnes de conformité + actions admin :
 *  - Créer (entreprise + période + nom)
 *  - Exporter (CSV / PDF / Les deux)
 *  - Télécharger un export historique
 *  - Envoyer au DRH (manuel)
 *  - Archiver
 *  - Configurer les colonnes CSV (ouvre ExportColumnsModal)
 *
 * Design : ivoire / or / charbon — cohérent AdminCompliance.jsx.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Download, Mail, Archive, Settings2, FileText, FileSpreadsheet,
  Loader2, ChevronLeft, ChevronRight, X, Send, ShieldCheck, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import ExportColumnsModal from './ExportColumnsModal';

const EXPORT_FORMATS = [
  { id: 'csv', label: 'CSV OPCO', icon: FileSpreadsheet },
  { id: 'pdf', label: 'PDF Premium', icon: FileText },
  { id: 'both', label: 'Les deux', icon: Download },
];

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d.length <= 10 ? `${d}T00:00:00` : d).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return d; }
}

function fmtDT(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return d; }
}

// ─── Create Campaign Modal ──────────────────────────────────────────────
function CreateCampaignModal({ open, onClose, onCreated, api, token, companies }) {
  const [name, setName] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setName(''); setCompanyId(''); setStart(''); setEnd('');
    }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !companyId || !start || !end) {
      toast.error('Tous les champs sont obligatoires.');
      return;
    }
    if (end < start) {
      toast.error('La date de fin doit être après la date de début.');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${api}/admin/compliance/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, company_id: companyId, period_start: start, period_end: end }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur création');
      toast.success('Campagne créée.');
      onCreated && onCreated(data);
      onClose && onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1F1A12]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      data-testid="create-campaign-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="bg-white border border-[#E8E2D2] rounded-2xl w-full max-w-lg shadow-2xl"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D2]">
          <h2 className="text-lg font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
            Nouvelle campagne
          </h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#F3EFE3] text-[#6B5E48]" data-testid="create-campaign-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
              Nom de la campagne
            </label>
            <input
              data-testid="create-campaign-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Q1 2026 — Acme"
              className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A] focus:bg-white"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
              Entreprise
            </label>
            {(companies || []).length > 0 ? (
              <select
                data-testid="create-campaign-company"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-white text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A]"
              >
                <option value="">Sélectionner…</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>{c.company_id}</option>
                ))}
              </select>
            ) : (
              <input
                data-testid="create-campaign-company-input"
                type="text"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                placeholder="bskt_…"
                className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] font-mono focus:outline-none focus:border-[#92710A] focus:bg-white"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
                Période · début
              </label>
              <input
                data-testid="create-campaign-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A] focus:bg-white"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
                Période · fin
              </label>
              <input
                data-testid="create-campaign-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A] focus:bg-white"
              />
            </div>
          </div>
        </div>
        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E8E2D2] bg-[#FBF9F3]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-full text-sm font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="create-campaign-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Créer
          </button>
        </footer>
      </form>
    </div>
  );
}

// ─── Export Modal (format picker) ───────────────────────────────────────
function ExportFormatModal({ open, onClose, onConfirm, busy }) {
  const [format, setFormat] = useState('both');
  useEffect(() => { if (open) setFormat('both'); }, [open]);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-[#1F1A12]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      data-testid="export-format-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white border border-[#E8E2D2] rounded-2xl w-full max-w-md shadow-2xl">
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D2]">
          <h2 className="text-lg font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
            Format d'export
          </h2>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#F3EFE3] text-[#6B5E48]" data-testid="export-format-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-6 py-5 space-y-3">
          <p className="text-xs text-[#6B5E48]">
            Choisis le format. Le PDF inclut un QR de vérification publique et la signature SHA-256.
          </p>
          {EXPORT_FORMATS.map((f) => {
            const Icon = f.icon;
            const active = format === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFormat(f.id)}
                data-testid={`export-format-${f.id}`}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                  active
                    ? 'border-[#C49B2A] bg-[#FFFCEC] text-[#1F1A12]'
                    : 'border-[#E8E2D2] bg-white text-[#6B5E48] hover:bg-[#FBF9F3]'
                }`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-[#92710A]' : 'text-[#A89F8A]'}`} />
                <div className="flex-1">
                  <div className="text-sm font-medium">{f.label}</div>
                  <div className="text-xs text-[#A89F8A] mt-0.5">
                    {f.id === 'csv' && 'Colonnes configurables · OPCO-ready'}
                    {f.id === 'pdf' && 'Page de garde · KPI · QR · SHA-256'}
                    {f.id === 'both' && 'CSV + PDF en une opération'}
                  </div>
                </div>
                {active && <CheckCircle2 className="w-4 h-4 text-[#92710A]" />}
              </button>
            );
          })}
        </div>
        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E8E2D2] bg-[#FBF9F3]">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 rounded-full text-sm font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40">
            Annuler
          </button>
          <button
            type="button"
            onClick={() => onConfirm(format)}
            disabled={busy}
            data-testid="export-format-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Générer
          </button>
        </footer>
      </div>
    </div>
  );
}

// ─── Send DRH Modal ─────────────────────────────────────────────────────
function SendDRHModal({ open, onClose, onConfirm, busy, campaignName, exportRecord }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [format, setFormat] = useState('both');

  useEffect(() => {
    if (open) {
      setTo('');
      setSubject(`Rapport de conformité COACHDIA — ${campaignName || ''}`);
      setMessage('Bonjour,\n\nVeuillez trouver en pièce jointe le rapport de conformité de la période.\n\nBien cordialement,\nCOACHDIA');
      setFormat(exportRecord?.format || 'both');
    }
  }, [open, campaignName, exportRecord]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    if (!to || !to.includes('@')) {
      toast.error('Email DRH invalide.');
      return;
    }
    onConfirm({ to_email: to, subject, message, format, export_id: exportRecord?.export_id });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1F1A12]/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
      data-testid="send-drh-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={submit} className="bg-white border border-[#E8E2D2] rounded-2xl w-full max-w-xl shadow-2xl">
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D2]">
          <div>
            <h2 className="text-lg font-light text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
              Envoyer au DRH
            </h2>
            <p className="text-xs text-[#6B5E48]">Envoi manuel. Aucune adresse n'est pré-remplie automatiquement.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#F3EFE3] text-[#6B5E48]" data-testid="send-drh-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
              Destinataire DRH
            </label>
            <input
              data-testid="send-drh-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="drh@entreprise.com"
              className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A] focus:bg-white"
              required
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
              Sujet
            </label>
            <input
              data-testid="send-drh-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A] focus:bg-white"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
              Message
            </label>
            <textarea
              data-testid="send-drh-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="w-full px-3 py-2.5 rounded-lg border border-[#E8E2D2] bg-[#FBF9F3] text-sm text-[#1F1A12] focus:outline-none focus:border-[#92710A] focus:bg-white"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.14em] text-[#6B5E48] font-semibold block mb-1.5">
              Pièces jointes
            </label>
            <div className="flex gap-2">
              {EXPORT_FORMATS.map((f) => {
                const active = format === f.id;
                const disabled = exportRecord && exportRecord.format !== 'both' && exportRecord.format !== f.id && f.id !== 'both';
                return (
                  <button
                    type="button"
                    key={f.id}
                    onClick={() => !disabled && setFormat(f.id)}
                    data-testid={`send-drh-format-${f.id}`}
                    disabled={disabled}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-[#FFFCEC] border-[#C49B2A] text-[#92710A]'
                        : 'bg-white border-[#E8E2D2] text-[#6B5E48] hover:bg-[#FBF9F3]'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
            {exportRecord && (
              <p className="text-[11px] text-[#A89F8A] mt-2">
                Utilise les fichiers de l'export <span className="font-mono">{exportRecord.export_id.slice(0, 12)}…</span> (généré le {fmtDT(exportRecord.generated_at)}).
              </p>
            )}
          </div>
        </div>
        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E8E2D2] bg-[#FBF9F3]">
          <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 rounded-full text-sm font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40">
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="send-drh-submit"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer
          </button>
        </footer>
      </form>
    </div>
  );
}

// ─── Main Tab ───────────────────────────────────────────────────────────
export default function CampaignsTab({ api, token, companies }) {
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('active');
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);  // {campaign, export}
  const [sendBusy, setSendBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) q.append('status', statusFilter);
      const r = await fetch(`${api}/admin/compliance/campaigns?${q.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Chargement échoué');
      const data = await r.json();
      setItems(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (format) => {
    if (!exportTarget) return;
    setExportBusy(true);
    try {
      const r = await fetch(`${api}/admin/compliance/campaigns/${exportTarget.id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ format }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Erreur génération');
      toast.success('Export généré.');
      setExportTarget(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExportBusy(false);
    }
  };

  const handleDownload = async (campaignId, exportId, type) => {
    try {
      const r = await fetch(
        `${api}/admin/compliance/campaigns/${campaignId}/export/${exportId}/download?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Téléchargement échoué');
      }
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition') || '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = (m && m[1]) || `export.${type}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSend = async (payload) => {
    if (!sendTarget) return;
    setSendBusy(true);
    try {
      const r = await fetch(`${api}/admin/compliance/campaigns/${sendTarget.campaign.id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Envoi échoué');
      toast.success(`Email envoyé à ${payload.to_email}.`);
      setSendTarget(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendBusy(false);
    }
  };

  const handleArchive = async (campaignId) => {
    if (!confirm('Archiver cette campagne ? Les exports historiques restent consultables.')) return;
    try {
      const r = await fetch(`${api}/admin/compliance/campaigns/${campaignId}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Archivage échoué');
      toast.success('Campagne archivée.');
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="compliance-campaigns-content">
      <ExportColumnsModal open={columnsOpen} onClose={() => setColumnsOpen(false)} api={api} token={token} />
      <CreateCampaignModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
        api={api} token={token}
        companies={companies}
      />
      <ExportFormatModal
        open={!!exportTarget}
        onClose={() => setExportTarget(null)}
        onConfirm={handleExport}
        busy={exportBusy}
      />
      <SendDRHModal
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        onConfirm={handleSend}
        busy={sendBusy}
        campaignName={sendTarget?.campaign?.name}
        exportRecord={sendTarget?.export}
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          {['active', 'archived'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { setStatusFilter(s); setPage(1); }}
              data-testid={`campaigns-status-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-[#1F1A12] text-[#F3E9C8] shadow-sm'
                  : 'text-[#6B5E48] hover:bg-[#F3EFE3]'
              }`}
            >
              {s === 'active' ? 'Actives' : 'Archivées'}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setColumnsOpen(true)}
            data-testid="open-columns-config"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white"
          >
            <Settings2 className="w-3.5 h-3.5" /> Colonnes CSV
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="campaigns-create-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-gradient-to-br from-[#D4AF37] to-[#C49B2A] text-[#1F1A12] shadow-[0_2px_8px_rgba(180,150,40,0.25)] hover:brightness-110"
          >
            <Plus className="w-3.5 h-3.5" /> Nouvelle campagne
          </button>
        </div>
      </div>

      {loading && !items ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-[#92710A] animate-spin" />
        </div>
      ) : !items?.items?.length ? (
        <div className="bg-white border border-[#E8E2D2] rounded-2xl py-12 text-center text-sm text-[#A89F8A]" data-testid="campaigns-empty">
          Aucune campagne {statusFilter === 'archived' ? 'archivée' : 'active'} pour le moment.
        </div>
      ) : (
        <div className="space-y-3">
          {items.items.map((c) => (
            <article
              key={c.id}
              className="bg-white border border-[#E8E2D2] rounded-2xl p-5 space-y-3"
              data-testid={`campaign-card-${c.id}`}
            >
              <header className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <h3 className="text-base font-semibold text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>
                    {c.name}
                  </h3>
                  <div className="text-xs text-[#6B5E48] mt-1 flex items-center gap-2 flex-wrap font-mono">
                    <span>{c.company_id}</span>
                    <span className="text-[#A89F8A]">·</span>
                    <span>{fmtDate(c.period_start)} → {fmtDate(c.period_end)}</span>
                  </div>
                  <div className="text-[11px] text-[#A89F8A] mt-1.5">
                    Créée par {c.created_by} · {fmtDT(c.created_at)}
                    {c.exports_count > 0 && ` · ${c.exports_count} export(s)`}
                    {c.deliveries_count > 0 && ` · ${c.deliveries_count} envoi(s) DRH`}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {c.status === 'active' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setExportTarget(c)}
                        data-testid={`campaign-export-btn-${c.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#1F1A12] text-[#F3E9C8] hover:brightness-110"
                      >
                        <Download className="w-3.5 h-3.5" /> Exporter
                      </button>
                      <button
                        type="button"
                        onClick={() => handleArchive(c.id)}
                        data-testid={`campaign-archive-btn-${c.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-[#FBF9F3]"
                      >
                        <Archive className="w-3.5 h-3.5" /> Archiver
                      </button>
                    </>
                  )}
                  {c.status === 'archived' && (
                    <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-[#F3EFE3] text-[#6B5E48] border border-[#E8E2D2]">
                      Archivée
                    </span>
                  )}
                </div>
              </header>

              {(c.exports || []).length > 0 && (
                <div className="rounded-xl border border-[#F0EADA] divide-y divide-[#F0EADA]" data-testid={`campaign-exports-${c.id}`}>
                  {c.exports.map((e) => (
                    <div key={e.export_id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-[#92710A]" />
                      <div className="flex-1 min-w-[150px] text-xs text-[#1F1A12]">
                        <span className="font-mono text-[10px] text-[#A89F8A]">{e.export_id.slice(0, 12)}…</span>
                        <span className="ml-2 uppercase text-[10px] font-bold tracking-wider text-[#92710A]">{e.format}</span>
                        <div className="text-[11px] text-[#6B5E48] mt-0.5">
                          Par {e.generated_by} · {fmtDT(e.generated_at)}
                          {e.signature_v1 && (
                            <span className="ml-2 font-mono text-[#A89F8A]" title={e.signature_v1}>
                              sig:{e.signature_v1.slice(0, 8)}…
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {(e.format === 'csv' || e.format === 'both') && (
                          <button
                            type="button"
                            onClick={() => handleDownload(c.id, e.export_id, 'csv')}
                            data-testid={`download-csv-${e.export_id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white"
                          >
                            <FileSpreadsheet className="w-3 h-3" /> CSV
                          </button>
                        )}
                        {(e.format === 'pdf' || e.format === 'both') && (
                          <button
                            type="button"
                            onClick={() => handleDownload(c.id, e.export_id, 'pdf')}
                            data-testid={`download-pdf-${e.export_id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium border border-[#E8E2D2] text-[#6B5E48] hover:bg-white"
                          >
                            <FileText className="w-3 h-3" /> PDF
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setSendTarget({ campaign: c, export: e })}
                          data-testid={`send-drh-btn-${e.export_id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium bg-[#FFFCEC] border border-[#E8DDB5] text-[#92710A] hover:bg-[#FBF1C5]"
                        >
                          <Mail className="w-3 h-3" /> DRH
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(c.deliveries || []).length > 0 && (
                <details className="text-xs">
                  <summary className="text-[#6B5E48] cursor-pointer hover:text-[#1F1A12]">
                    Historique envois DRH ({c.deliveries.length})
                  </summary>
                  <ul className="mt-2 space-y-1.5 pl-3 border-l border-[#E8E2D2]">
                    {c.deliveries.map((d) => (
                      <li key={d.delivery_id} className="text-[11px] text-[#6B5E48]">
                        <Mail className="inline w-3 h-3 mr-1" />
                        {fmtDT(d.sent_at)} · {d.to_email} · {d.format.toUpperCase()} ·
                        <span className={d.ok ? 'text-[#1F6F3A] ml-1' : 'text-[#9A1C1C] ml-1'}>
                          {d.ok ? 'envoyé' : 'échec'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </article>
          ))}

          {items.total_pages > 1 && (
            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="p-2 rounded-lg border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
                data-testid="campaigns-pager-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-[#6B5E48] tabular-nums">
                Page <span className="font-semibold text-[#1F1A12]">{page}</span> / {items.total_pages}
              </span>
              <button
                type="button"
                onClick={() => setPage(Math.min(items.total_pages, page + 1))}
                disabled={page >= items.total_pages}
                className="p-2 rounded-lg border border-[#E8E2D2] text-[#6B5E48] hover:bg-white disabled:opacity-40"
                data-testid="campaigns-pager-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
